import React, { useState } from "react";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/components/ui/calendar";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function DateFilter({
  value,
  onChange,
  customDateRange,
  onCustomDateRangeChange,
}) {
  const { t } = useLanguage();
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);

  const handleSelectChange = (newValue) => {
    if (newValue === "custom") {
      onChange("custom");
      // Open popover when custom is selected
      setTimeout(() => setIsCustomRangeOpen(true), 0);
    } else {
      setIsCustomRangeOpen(false);
      onChange(newValue);
    }
  };

  const handleCustomDateRangeSelect = (range) => {
    const newRange = range || { from: null, to: null };
    onCustomDateRangeChange(newRange);

    // If both dates are selected, close the popover
    if (newRange.from && newRange.to) {
      // Small delay to allow the calendar to update before closing
      setTimeout(() => {
        setIsCustomRangeOpen(false);
      }, 100);
    }
  };

  const handlePopoverOpenChange = (open) => {
    setIsCustomRangeOpen(open);
  };

  const getDisplayText = () => {
    switch (value) {
      case "today":
        return t("thisDate");
      case "week":
        return t("thisWeek");
      case "month":
        return t("thisMonth");
      case "year":
        return t("thisYear");
      case "custom":
        if (customDateRange?.from && customDateRange?.to) {
          return `${format(customDateRange.from, "MMM d")}-${format(customDateRange.to, "MMM d, yyyy")}`;
        }
        return t("customRange");
      default:
        return t("thisDate");
    }
  };

  const getCustomRangeButtonText = () => {
    if (customDateRange?.from && customDateRange?.to) {
      return `${format(customDateRange.from, "MMM d")} - ${format(customDateRange.to, "MMM d, yyyy")}`;
    }

    if (customDateRange?.from) {
      return format(customDateRange.from, "MMM d, yyyy");
    }

    return t("selectRange");
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={value} onValueChange={handleSelectChange}>
        <SelectTrigger className="w-[170px] bg-[#070f1f] text-[#e2e8f0] border border-[#1e3a5f] rounded-md hover:border-cyan-400 hover:text-cyan-400 transition-colors">
          <Calendar className="h-4 w-4 mr-2 text-[#94a3b8]" />
          <SelectValue>{getDisplayText()}</SelectValue>
        </SelectTrigger>
        <SelectContent className="bg-[#070f1f] border border-[#1e3a5f] text-[#e2e8f0] rounded-md">
          <SelectItem value="today" className="cursor-pointer hover:bg-[#0e4d6e] hover:text-[#67e8f9]">{t("thisDate")}</SelectItem>
          <SelectItem value="week" className="cursor-pointer hover:bg-[#0e4d6e] hover:text-[#67e8f9]">{t("thisWeek")}</SelectItem>
          <SelectItem value="month" className="cursor-pointer hover:bg-[#0e4d6e] hover:text-[#67e8f9]">{t("thisMonth")}</SelectItem>
          <SelectItem value="year" className="cursor-pointer hover:bg-[#0e4d6e] hover:text-[#67e8f9]">{t("thisYear")}</SelectItem>
          <SelectItem value="custom" className="cursor-pointer hover:bg-[#0e4d6e] hover:text-[#67e8f9]">{t("customRange")}</SelectItem>
        </SelectContent>
      </Select>

      {value === "custom" && (
        <Popover open={isCustomRangeOpen} onOpenChange={handlePopoverOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="gap-2 min-w-[230px] justify-start bg-[#070f1f] text-[#e2e8f0] border border-[#1e3a5f] hover:border-cyan-400 hover:text-cyan-400 transition-colors"
              onClick={() => setIsCustomRangeOpen(!isCustomRangeOpen)}
            >
              <Calendar className="h-4 w-4" />
              {getCustomRangeButtonText()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#070f1f] border border-[#1e3a5f] text-[#e2e8f0] rounded-md" align="start">
            <CalendarComponent
              mode="range"
              selected={customDateRange || { from: null, to: null }}
              onSelect={handleCustomDateRangeSelect}
              numberOfMonths={2}
              className="bg-[#070f1f] text-[#e2e8f0] border border-[#1e3a5f]"
            />
          </PopoverContent>
        </Popover>
      )}

    </div>
  );
}





